import type { ErrorLike } from '@apollo/client';
import { Box, Button, GlobalStyles, Theme } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { TypeOptions } from 'react-toastify/dist/types';

import { ReturnComponent } from './utils/reactTypes';

// Overrides for react-toastify's look (previously toast-styles.css). Rendered alongside the
// toast container so it loads with it. Doubled class selectors (e.g. `.Toastify__toast.
// Toastify__toast`) are kept to preserve the original specificity over the library defaults.
// Toast type colors come from the theme's status palette so every toast matches the rest
//  of the app.
const toastGlobalStyles = (
    <GlobalStyles
        styles={({ palette }: Theme) => ({
            // `:root:root` (specificity 0,2,0) so these variable overrides beat the library's
            //  own `:root` defaults regardless of stylesheet injection order.
            ':root:root': {
                '--toastify-color-info': palette.info.main,
                '--toastify-color-success': palette.success.main,
                '--toastify-color-warning': palette.warning.main,
                '--toastify-color-error': palette.error.main,
                '--toastify-icon-color-info': palette.info.contrastText,
                '--toastify-icon-color-success': palette.success.contrastText,
                '--toastify-icon-color-warning': palette.warning.contrastText,
                '--toastify-icon-color-error': palette.error.contrastText,
                '--toastify-toast-width': '20em',
                '--toastify-toast-min-height': '48px',
                '--toastify-toast-max-height': '800px',
                '--toastify-font-family': 'sans-serif',
                '--toastify-z-index': '1600',
                '--toastify-color-light': '#fafafa',
                '--toastify-color-dark': '#323232',
                '--toastify-color-transparent': 'rgba(255, 255, 255, 0.7)',
                '--toastify-color-progress-light': 'var(--toastify-color-light)',
                '--toastify-color-progress-dark': 'var(--toastify-color-dark)',
                '--toastify-color-progress-info': 'var(--toastify-color-info)',
                '--toastify-color-progress-success': 'var(--toastify-color-success)',
                '--toastify-color-progress-warning': 'var(--toastify-color-warning)',
                '--toastify-color-progress-error': 'var(--toastify-color-error)',
                '--toastify-text-color-light': '#757575',
                '--toastify-text-color-dark': '#fff',
                '--toastify-text-color-info': palette.info.contrastText,
                '--toastify-text-color-success': palette.success.contrastText,
                '--toastify-text-color-warning': palette.warning.contrastText,
                '--toastify-text-color-error': palette.error.contrastText,
                '--toastify-spinner-color': '#616161',
                '--toastify-spinner-color-empty-area': '#e0e0e0',
            },
            '.Toastify__toast.Toastify__toast': {
                fontSize: '0.875rem',
                lineHeight: '1.25em',
                borderRadius: '8px',
                boxShadow:
                    '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
            },
            // Doubled selectors (specificity 0,2,0) so the per-type background beats the
            //  library's `.Toastify__toast-theme--light` background regardless of injection order.
            '.Toastify__toast--info.Toastify__toast--info': {
                background: 'var(--toastify-color-info)',
                color: 'var(--toastify-text-color-info)',
            },
            '.Toastify__toast--success.Toastify__toast--success': {
                background: 'var(--toastify-color-success)',
                color: 'var(--toastify-text-color-success)',
            },
            '.Toastify__toast--warning.Toastify__toast--warning': {
                background: 'var(--toastify-color-warning)',
                color: 'var(--toastify-text-color-warning)',
            },
            '.Toastify__toast--error.Toastify__toast--error': {
                background: 'var(--toastify-color-error)',
                color: 'var(--toastify-text-color-error)',
            },
            '.Toastify__close-button.Toastify__close-button': { alignSelf: 'center' },
            '.newToast .Toastify__close-button': { alignSelf: 'flex-start', marginTop: '6px' },
        })}
    />
);

//////////////////////////////////////////////////////////////////////////////
// Internal helpers.
//////////////////////////////////////////////////////////////////////////////

// Q: How do we deduplicate toasts?
//
// A: We use the `toastId` option of react-toastify.
//
// react-toastify won’t display a toast if a toast with the same ID has already
//  being displayed (see https://fkhadra.github.io/react-toastify/use-a-custom-id).
//
// By making the message the toast ID, the same message will never be displayed
//  in multiple toasts at the same time.
//
// Toast with abandon, lads!
//
/*
 _..--""````""--.._
(_                _;..--""````""--.._
\ ```"""----"""```(_                _)
 '-.            .-\ ```"""----"""``` /
    `\        /`   '-.            .-'
      '-.__.-'        `\        /`
         ||             '-.__.-'
         ||                ||
         ||                ||
         ||                ||
    _..--||--.._           ||
   (_          _)     _..--||--.._
     ```""""```      (_          _)
                       ```""""```
*/

type AxleToastOptions = {
    deduplicate?: boolean;
    autoClose?: number | false | undefined;
    action?: { label: string; onClick: () => void };
};

const ToastWithAction = ({
    message,
    action,
    closeToast,
}: {
    message: string;
    action: NonNullable<AxleToastOptions['action']>;
    closeToast?: () => void;
}): ReturnComponent => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>{message}</Box>
        <Button
            size='small'
            color='inherit'
            sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            onClick={() => {
                action.onClick();
                closeToast?.();
            }}
        >
            {action.label}
        </Button>
    </Box>
);

export const genericToast = (
    message: string,
    type: TypeOptions,
    { deduplicate = true, autoClose, action }: AxleToastOptions = {},
) => {
    const content = action
        ? ({ closeToast }: { closeToast?: () => void }) => (
              <ToastWithAction message={message} action={action} closeToast={closeToast} />
          )
        : message;
    toast(content, {
        type,
        autoClose,
        ...(deduplicate && { toastId: [type, message].join(',') }),
    });
};

//////////////////////////////////////////////////////////////////////////////
// Public functions for toasting.
//////////////////////////////////////////////////////////////////////////////

export const successToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.SUCCESS, options);
};

export const warningToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.WARNING, options);
};

export const errorToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.ERROR, options);
};

export const onError = (error: ErrorLike) => {
    errorToast(error.message);
};

//////////////////////////////////////////////////////////////////////////////
// Public toast container.
//////////////////////////////////////////////////////////////////////////////

export const AxleToastContainer = (): ReturnComponent => (
    <>
        {toastGlobalStyles}
        <ToastContainer
            // Container options:
            limit={3} // Only show at most 3 toasts at a time. The rest will wait their turn.
            newestOnTop={false}
            rtl={false}
            // Defaults for toast options.
            position={toast.POSITION.BOTTOM_CENTER}
            autoClose={3000} // Stay on screen for 3 seconds.
            hideProgressBar // Otherwise, shows a countdown timer progress bar; kinda stressful and distracting.
            closeOnClick={true} // Allow clicking the toast to dismiss it.
            draggable // Allow dragging the toast to dismiss it.
            draggablePercent={60} // Make dragging to dismiss easier.
            draggableDirection={'y'} // Make it vertical dragging instead of horizontal.
            pauseOnFocusLoss={true} // Stop the countdown timer if the window loses focus.
            pauseOnHover={true} // Stop the countdown timer if the cursor is on the toast.
            // The rest of the styling is in `toastGlobalStyles` above.
            style={{ maxWidth: '80vw', width: '25rem' }}
        />
    </>
);
